import {
  Card,
  Title1,
  Title3,
  Text,
  Spinner,
  ProgressBar,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import { MirrorFreshnessBanner } from '../components/common/MirrorFreshnessBanner';
import {
  People24Regular,
  CalendarLtr24Regular,
  CheckmarkCircle24Regular,
  Warning24Regular,
  ClockRegular,
  TaskListLtr24Regular,
  ArrowTrendingRegular,
  Open16Regular,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { useBookkeepingClients, useUpcomingAppointments } from '../hooks/useClients';
import { useClientReviews } from '../hooks/useReviews';
import { PageSkeleton } from '../components/common/PageSkeleton';
import { LoadFailed } from '../components/common/LoadFailed';
import { useBookkeepingDashboard, useWorkloads } from '../api/bookkeeping';
import { useSemanticColors, type SemanticColor } from '../theme/semanticColors';
import { clientReviewsPath, taxLifecycleClientReturnsUrl } from '../utils/crossAppLinks';
import { reviewPeriodLabel } from '../utils/reviewPeriod';
import type { Appointment } from '../types';
import { useTranslation } from '../i18n';
import { formatInstant } from '../i18n/dates';
import { taskWindowLabel, sinceStartOfYear } from '../utils/taskWindow';

const useStyles = makeStyles({
  // Hover affordance for clickable tiles — token-driven so dark mode stays consistent.
  clickableCard: {
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  // Inline row links (tax-appointment rows). Real <a>/<Link> elements — focusable and
  // Enter-activatable for free, which a click-handler <div> would not be.
  rowLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '4px',
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase200,
    textDecorationLine: 'none',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'underline' },
    ':focus-visible': { textDecorationLine: 'underline' },
  },
});

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: SemanticColor;
  subtitle?: string;
  /** When set, the whole tile becomes a link to this route/anchor. */
  to?: string;
  /** The tile's data failed to load: it says so, with a Retry, instead of showing a number (and is not a link). */
  failed?: boolean;
  onRetry?: () => void;
}

/** A widget's link, dropped while the widget shows a load failure so its Retry button is not inside a link. */
function TileLink({ to, children }: { to?: string; children: React.ReactElement }) {
  if (!to) return children;
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {children}
    </Link>
  );
}

/** 48px icon chip used by the stat tiles and the bookkeeping widgets below. */
function iconChipStyle(color: SemanticColor): React.CSSProperties {
  return {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: color.fill,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color.onFill,
  };
}

function StatCard({ title, value, icon, color, subtitle, to: link, failed, onRetry }: StatCardProps) {
  const to = failed ? undefined : link;
  const styles = useStyles();
  const card = (
    <Card style={{ padding: '20px' }} className={to ? styles.clickableCard : undefined}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <div style={iconChipStyle(color)}>
          {icon}
        </div>
        <div>
          <Text
            size={200}
            style={{ color: tokens.colorNeutralForeground3, display: 'block' }}
          >
            {title}
          </Text>
          {failed ? (
            <div style={{ marginTop: '6px' }}><LoadFailed onRetry={onRetry} /></div>
          ) : (
            <Text size={600} weight="semibold" style={{ display: 'block' }}>
              {value}
            </Text>
          )}
          {subtitle && !failed && (
            <Text
              size={200}
              style={{ color: tokens.colorNeutralForeground4, display: 'block' }}
            >
              {subtitle}
            </Text>
          )}
        </div>
      </div>
    </Card>
  );

  if (!to) return card;
  // In-page anchors (e.g. the appointments panel) use a plain <a> so the href stays a bare
  // fragment and the browser scrolls; route links use react-router's Link.
  if (to.startsWith('#')) {
    return (
      <a href={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        {card}
      </a>
    );
  }
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {card}
    </Link>
  );
}

/**
 * Resolves the client label for an appointment row. When both the expanded account name AND the
 * regarding lookup's formatted value are missing the appointment isn't linked to a client (e.g.
 * SQL-core rows whose RegardingCompanyId sync is null) — render a subtle "Client not linked"
 * (`notLinked`, in the UI language) instead of the old bare "No Client".
 */
function appointmentClientLabel(apt: Appointment, notLinked: string): { text: string; linked: boolean } {
  const name =
    apt.regardingobjectid_account?.name
    || apt['_regardingobjectid_value@OData.Community.Display.V1.FormattedValue'];
  return name ? { text: name, linked: true } : { text: notLinked, linked: false };
}

/**
 * Reviews-per-client-name index used to gate the "Reviews" link on tax-appointment rows.
 *
 * ELIGIBILITY RULE: the link appears only when this client actually HAS at least one review — the
 * cheapest honest signal that we did the bookkeeping worth reviewing (a review only exists off a
 * `cr8f7_bookkeeping` record). No review ⇒ nothing to link to, so no link, rather than a link into
 * an empty list. Keyed by lower-cased client name because that's also how the link filters
 * (`filter.client.eq`, case-insensitive) — so a rendered link is always a non-empty destination.
 */
function reviewCountsByClientName(
  reviews: { clientName?: string }[] | undefined
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const r of reviews ?? []) {
    const key = r.clientName?.trim().toLowerCase();
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export default function DashboardPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: clients, isLoading: clientsLoading, error: clientsError } = useBookkeepingClients();
  // Every source but the clients (which gate the whole page) reports its own failure in its own tile or panel.
  const {
    data: appointments, isLoading: appointmentsLoading, isError: appointmentsFailed, refetch: refetchAppointments,
  } = useUpcomingAppointments(14);
  const {
    data: taxAppointments, isLoading: taxAppointmentsLoading, isError: taxAppointmentsFailed, refetch: refetchTaxAppointments,
  } = useUpcomingAppointments(30, { taxOnly: true });
  const { data: reviews, isLoading: reviewsLoading, isError: reviewsFailed, refetch: refetchReviews } = useClientReviews();
  const { data: bookkeepingSummary, isError: summaryFailed, refetch: refetchSummary } = useBookkeepingDashboard();
  const { data: workloads, isError: workloadsFailed, refetch: refetchWorkloads } = useWorkloads();
  const sem = useSemanticColors();

  // Soonest-first tax appointments for the "Upcoming Tax Appointments" panel.
  const sortedTaxAppointments = [...(taxAppointments ?? [])].sort(
    (a, b) => new Date(a.scheduledstart).getTime() - new Date(b.scheduledstart).getTime(),
  );

  // Last 5 completed reviews, newest-first (pending/in-progress have no completedAt → excluded).
  const recentlyCompletedReviews = (reviews ?? [])
    .filter(r => r.status === 'completed' && r.completedAt)
    .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime())
    .slice(0, 5);

  // Gates the tax-appointment rows' "Reviews" link (see reviewCountsByClientName). Reuses the
  // reviews the panel above already fetched — no extra request.
  const reviewCounts = reviewCountsByClientName(reviews);

  if (clientsLoading) {
    return <PageSkeleton variant="tiles" />;
  }

  if (clientsError) {
    return (
      <Card style={{ padding: '24px', textAlign: 'center' }}>
        <Warning24Regular style={{ fontSize: '48px', color: tokens.colorPaletteRedForeground1 }} />
        <Title3 style={{ marginTop: '16px' }}>{t('dashboard.errorTitle')}</Title3>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          {clientsError instanceof Error ? clientsError.message : t('dashboard.errorFallback')}
        </Text>
      </Card>
    );
  }

  const totalClients = clients?.length || 0;
  // The app's own reviews that need work now: ready + in progress, the derived lifecycle (CLAUDE.md: default counts use
  // ready + in-progress, never the raw stored status). It used to count the CRM's `qtrreviewdone` flag, which is set
  // for no client, so the tile read the client total (234 of 234 on 2026-10-28; Christian ruled the fix the same day).
  const reviewsDue = (reviews ?? []).filter(r => r.lifecycleStatus === 'ready' || r.lifecycleStatus === 'in-progress').length;
  const upcomingAppointmentsCount = appointments?.length || 0;

  // Calculate complexity distribution
  const highComplexity = clients?.filter(c => (c.cr8f7_avgmonthlytransactions || 0) > 300).length || 0;
  const mediumComplexity = clients?.filter(c => {
    const txns = c.cr8f7_avgmonthlytransactions || 0;
    return txns > 100 && txns <= 300;
  }).length || 0;
  const lowComplexity = totalClients - highComplexity - mediumComplexity;

  // Status distribution — derived (see deriveStatusCategory); there is no
  // status column in Dataverse, so we bucket on the enriched statusCategory.
  const inProgressCount = clients?.filter(c => c.statusCategory === 'in-progress').length || 0;
  const waitingOnClientCount = clients?.filter(c => c.statusCategory === 'waiting').length || 0;
  const notStartedCount = clients?.filter(c => c.statusCategory === 'not-started').length || 0;
  const completedCount = clients?.filter(c => c.statusCategory === 'completed').length || 0;

  // Live task totals — windowed open/completed counts (server-computed in ClientFunctions via
  // GetTaskCountsForAccountsAsync). The window's first DAY comes from the API so the label tracks the
  // real cutoff instead of a hardcoded year, and is worded here in the UI language (the server's
  // English `taskCountPeriod` only for an API that predates `taskCountSince`).
  const taskPeriodLabel = taskWindowLabel(
    clients?.[0]?.taskCountSince,
    clients?.[0]?.taskCountPeriod,
    sinceStartOfYear(),
  );
  const totalOutstandingTasks = clients?.reduce((sum, c) => sum + (c.filteredOutstandingTasks || 0), 0) || 0;
  const totalCompletedTasks = clients?.reduce((sum, c) => sum + (c.filteredCompletedTasks || 0), 0) || 0;

  return (
    <div>
      <MirrorFreshnessBanner />
      <div className="page-header">
        <Title1>{t('dashboard.title')}</Title1>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          {t('dashboard.subtitle')}
        </Text>
      </div>

      {/* Primary Stats */}
      <div className="stats-grid">
        <StatCard
          title={t('dashboard.tiles.totalClients')}
          value={totalClients}
          icon={<People24Regular />}
          color={sem.brand}
          subtitle={t('dashboard.tiles.totalClientsSub')}
          to="/clients"
        />
        <StatCard
          title={t('dashboard.tiles.inProgress')}
          value={inProgressCount}
          icon={<ClockRegular />}
          color={sem.brand}
          subtitle={t('dashboard.tiles.inProgressSub')}
          to="/clients?view=in-progress"
        />
        <StatCard
          title={t('dashboard.tiles.waiting')}
          value={waitingOnClientCount}
          icon={<Warning24Regular />}
          color={sem.warning}
          subtitle={t('dashboard.tiles.waitingSub')}
          to="/clients?view=waiting"
        />
        <StatCard
          title={t('dashboard.tiles.outstanding')}
          value={totalOutstandingTasks}
          icon={<TaskListLtr24Regular />}
          color={sem.danger}
          subtitle={t('dashboard.tiles.outstandingSub', { period: taskPeriodLabel, count: totalCompletedTasks })}
          to="/clients?sort=tasks&dir=desc"
        />
      </div>

      {/* Secondary Stats */}
      <div className="stats-grid" style={{ marginTop: '16px' }}>
        <StatCard
          title={t('dashboard.tiles.notStarted')}
          value={notStartedCount}
          icon={<Warning24Regular />}
          color={sem.neutral}
          subtitle={t('dashboard.tiles.notStartedSub')}
          to="/clients?filter.status.eq=not-started"
        />
        <StatCard
          title={t('dashboard.tiles.completed')}
          value={completedCount}
          icon={<CheckmarkCircle24Regular />}
          color={sem.success}
          subtitle={t('dashboard.tiles.completedSub')}
          to="/clients?view=completed"
        />
        <StatCard
          title={t('dashboard.tiles.reviewsDue')}
          // "…" until the reviews have loaded: a tile reading 0 while loading is a false count (P6, 2026-10-30).
          value={reviewsLoading ? '…' : reviewsDue}
          icon={<CalendarLtr24Regular />}
          color={sem.purple}
          subtitle={t('dashboard.tiles.reviewsDueSub')}
          to="/reviews?view=all&filter.status.in=ready,in-progress"
          failed={reviewsFailed}
          onRetry={() => refetchReviews()}
        />
        <StatCard
          title={t('dashboard.tiles.appointments')}
          value={appointmentsLoading ? '…' : upcomingAppointmentsCount}
          icon={<CalendarLtr24Regular />}
          color={sem.purple}
          to="#upcoming-appointments"
          subtitle={t('dashboard.tiles.appointmentsSub')}
          failed={appointmentsFailed}
          onRetry={() => refetchAppointments()}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginTop: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Upcoming Tax Appointments (soonest first) */}
          <Card style={{ padding: '20px' }}>
            <Title3 style={{ marginBottom: '16px' }}>{t('dashboard.tax.title')}</Title3>
            {taxAppointmentsLoading ? (
              <Spinner size="small" />
            ) : taxAppointmentsFailed ? (
              <LoadFailed onRetry={() => refetchTaxAppointments()} />
            ) : sortedTaxAppointments.length === 0 ? (
              <Text style={{ color: tokens.colorNeutralForeground3 }}>
                {t('dashboard.tax.empty')}
              </Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sortedTaxAppointments.slice(0, 8).map(apt => {
                  const label = appointmentClientLabel(apt, t('dashboard.clientNotLinked'));
                  // Both row links key off the client NAME, so they only make sense once the
                  // appointment resolves to a client.
                  const reviewCount = label.linked
                    ? reviewCounts.get(label.text.trim().toLowerCase()) ?? 0
                    : 0;
                  return (
                    <div
                      key={apt.activityid}
                      style={{
                        padding: '12px',
                        backgroundColor: tokens.colorNeutralBackground2,
                        borderRadius: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                        }}
                      >
                        <div>
                          <Text
                            weight={label.linked ? 'semibold' : 'regular'}
                            style={{
                              display: 'block',
                              color: label.linked ? undefined : tokens.colorNeutralForeground4,
                              fontStyle: label.linked ? undefined : 'italic',
                            }}
                          >
                            {label.text}
                          </Text>
                          {apt.cr8f7_servicesimpletext && (
                            <Text size={200} style={{ color: tokens.colorBrandForeground1, display: 'block' }}>
                              {apt.cr8f7_servicesimpletext}
                            </Text>
                          )}
                        </div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>
                          {formatInstant(apt.scheduledstart, 'weekdayTime')}
                        </Text>
                      </div>
                      {label.linked && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            marginTop: '8px',
                          }}
                        >
                          {/* Out to ptc-tax-lifecycle: that client's tax returns for the current
                              prep year. It's the client's returns list, not this one appointment's
                              record — see src/utils/crossAppLinks.ts for why a per-appointment
                              record URL isn't constructible from the browser. */}
                          <a
                            className={styles.rowLink}
                            href={taxLifecycleClientReturnsUrl(label.text)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('dashboard.tax.lifecycleAria', { client: label.text })}
                          >
                            {t('dashboard.tax.lifecycle')}
                            <Open16Regular aria-hidden />
                          </a>
                          {reviewCount > 0 && (
                            <Link
                              className={styles.rowLink}
                              to={clientReviewsPath(label.text)}
                              aria-label={t('dashboard.tax.reviewsAria', { count: reviewCount, client: label.text })}
                            >
                              {t('dashboard.tax.reviewsLink', { count: reviewCount })}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Recently Completed Reviews (last 5, newest first) */}
          <Card style={{ padding: '20px' }}>
            <Title3 style={{ marginBottom: '16px' }}>{t('dashboard.recent.title')}</Title3>
            {reviewsFailed ? (
              <LoadFailed onRetry={() => refetchReviews()} />
            ) : reviewsLoading ? (
              <Spinner size="tiny" />
            ) : recentlyCompletedReviews.length === 0 ? (
              <Text style={{ color: tokens.colorNeutralForeground3 }}>
                {t('dashboard.recent.empty')}
              </Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {recentlyCompletedReviews.map(review => {
                  // WHICH period was reviewed — the four-month block (backend name: trimester) plus
                  // the exact stored range, so a row is never ambiguous about what it covered.
                  const periodLabel = reviewPeriodLabel(review.periodStart, review.periodEnd);
                  return (
                    <Link
                      key={review.id}
                      to={`/reviews/${review.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <div
                        className={styles.clickableCard}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: tokens.colorNeutralBackground2,
                          borderRadius: '4px',
                        }}
                      >
                        <div>
                          <Text weight="semibold" style={{ display: 'block' }}>
                            {review.clientName}
                          </Text>
                          {periodLabel && (
                            <Text size={200} style={{ color: tokens.colorNeutralForeground2, display: 'block' }}>
                              {t('dashboard.recent.period', { period: periodLabel })}
                            </Text>
                          )}
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                            {t('dashboard.recent.reviewedBy', { name: review.supervisorName || t('common.unassigned') })}
                          </Text>
                        </div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>
                          {review.completedAt && formatInstant(review.completedAt, 'dayYear')}
                        </Text>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Upcoming Appointments (next 14 days) — target of the Appointments tile anchor */}
        <Card id="upcoming-appointments" style={{ padding: '20px' }}>
          <Title3 style={{ marginBottom: '16px' }}>{t('dashboard.appts.title')}</Title3>
          {appointmentsLoading ? (
            <Spinner size="small" />
          ) : appointmentsFailed ? (
            <LoadFailed onRetry={() => refetchAppointments()} />
          ) : appointments?.length === 0 ? (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>
              {t('dashboard.appts.empty')}
            </Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {appointments?.slice(0, 6).map(apt => {
                const label = appointmentClientLabel(apt, t('dashboard.clientNotLinked'));
                return (
                  <div
                    key={apt.activityid}
                    style={{
                      padding: '12px',
                      backgroundColor: tokens.colorNeutralBackground2,
                      borderRadius: '4px',
                    }}
                  >
                    <Text
                      weight={label.linked ? 'semibold' : 'regular'}
                      style={{
                        display: 'block',
                        color: label.linked ? undefined : tokens.colorNeutralForeground4,
                        fontStyle: label.linked ? undefined : 'italic',
                      }}
                    >
                      {label.text}
                    </Text>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground2, display: 'block' }}>
                      {apt.subject}
                    </Text>
                    {apt.cr8f7_servicesimpletext && (
                      <Text size={200} style={{ color: tokens.colorBrandForeground1, display: 'block' }}>
                        {t('dashboard.appts.reason', { reason: apt.cr8f7_servicesimpletext })}
                      </Text>
                    )}
                    {apt.appointment_activity_parties && apt.appointment_activity_parties.length > 0 && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground2, display: 'block' }}>
                        {t('dashboard.appts.attendees', {
                          names: apt.appointment_activity_parties
                            .map(p => p['_partyid_value@OData.Community.Display.V1.FormattedValue'] || t('common.unknown'))
                            .join(', '),
                        })}
                      </Text>
                    )}
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {formatInstant(apt.scheduledstart, 'weekdayTime')}
                      {apt.location && ` - ${apt.location}`}
                    </Text>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Complexity Distribution */}
      <Card style={{ padding: '20px', marginTop: '24px' }}>
        <Title3 style={{ marginBottom: '16px' }}>{t('dashboard.complexity.title')}</Title3>
        <div style={{ display: 'flex', gap: '24px' }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                height: '24px',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${totalClients > 0 ? (lowComplexity / totalClients) * 100 : 0}%`,
                  backgroundColor: sem.success.solid,
                }}
              />
              <div
                style={{
                  width: `${totalClients > 0 ? (mediumComplexity / totalClients) * 100 : 0}%`,
                  backgroundColor: sem.warning.solid,
                }}
              />
              <div
                style={{
                  width: `${totalClients > 0 ? (highComplexity / totalClients) * 100 : 0}%`,
                  backgroundColor: sem.danger.solid,
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <Text size={200}>
                <span style={{ color: sem.success.text }}>
                  {t('dashboard.complexity.band', { band: t('complexity.label.low'), count: lowComplexity })}
                </span>
              </Text>
              <Text size={200}>
                <span style={{ color: sem.warning.text }}>
                  {t('dashboard.complexity.band', { band: t('complexity.label.medium'), count: mediumComplexity })}
                </span>
              </Text>
              <Text size={200}>
                <span style={{ color: sem.danger.text }}>
                  {t('dashboard.complexity.band', { band: t('complexity.label.high'), count: highComplexity })}
                </span>
              </Text>
            </div>
          </div>
        </div>
        {/* A caption naming who handles which complexity band used to live here. It named a
            supervisor who left in March and was rendered, in the present tense, to every user for
            four months. It is deleted rather than rewritten: this app has no current, verified
            statement of who handles what — reviewer assignment lives per-client in
            core.ReviewerAssignment (Staff Settings), not in a complexity split — so any replacement
            sentence would be the same mistake pointed the other way. The bar and its Low/Medium/High
            counts are the measurement; they need no gloss. If a staffing statement is ever wanted
            here, derive it from the assignment data rather than typing it in. */}
      </Card>

      {/* Bookkeeping Supervision Widgets */}
      <div
        className="stats-grid"
        style={{ marginTop: '24px', gridTemplateColumns: 'repeat(3, 1fr)' }}
      >
        {/* Overdue bookkeeping clients */}
        <TileLink to={summaryFailed ? undefined : '/bookkeeping?status=overdue'}>
          <Card style={{ padding: '20px', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={iconChipStyle(sem.danger)}>
                <Warning24Regular />
              </div>
              <div>
                <Text
                  size={200}
                  style={{ color: tokens.colorNeutralForeground3, display: 'block' }}
                >
                  {t('dashboard.widgets.overdue')}
                </Text>
                <Text size={600} weight="semibold" style={{ display: 'block' }}>
                  {/* clientsByStatus.overdue = number of CLIENTS classified overdue — matches
                      the tile label and the /bookkeeping?status=overdue list this card links
                      to. overdueCount is the SUM of overdue TASKS across clients (can exceed
                      the client total, e.g. 242 tasks vs 240 clients on 2026-07-04). */}
                  {summaryFailed ? null : bookkeepingSummary?.clientsByStatus?.overdue ?? '-'}
                </Text>
                {summaryFailed ? (
                  <LoadFailed onRetry={() => refetchSummary()} />
                ) : (
                  <Text
                    size={200}
                    style={{ color: tokens.colorNeutralForeground4, display: 'block' }}
                  >
                    {t('dashboard.widgets.overdueSub')}
                  </Text>
                )}
              </div>
            </div>
          </Card>
        </TileLink>

        {/* This month's progress */}
        <TileLink to={summaryFailed ? undefined : '/bookkeeping?view=monthly'}>
          <Card style={{ padding: '20px', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={iconChipStyle(sem.brand)}>
                <ArrowTrendingRegular />
              </div>
              <div style={{ flex: 1 }}>
                <Text
                  size={200}
                  style={{ color: tokens.colorNeutralForeground3, display: 'block' }}
                >
                  {t('dashboard.widgets.monthProgress')}
                </Text>
                {summaryFailed ? (
                  <div style={{ marginTop: '6px' }}><LoadFailed onRetry={() => refetchSummary()} /></div>
                ) : (
                  <>
                <Text size={600} weight="semibold" style={{ display: 'block' }}>
                  {bookkeepingSummary
                    ? `${Math.round(bookkeepingSummary.currentMonthProgressPercent)}%`
                    : '-'}
                </Text>
                <div style={{ marginTop: '6px', maxWidth: '220px' }}>
                  <ProgressBar
                    value={(bookkeepingSummary?.currentMonthProgressPercent ?? 0) / 100}
                    color={
                      (bookkeepingSummary?.currentMonthProgressPercent ?? 0) >= 80
                        ? 'success'
                        : (bookkeepingSummary?.currentMonthProgressPercent ?? 0) >= 40
                        ? 'warning'
                        : 'error'
                    }
                  />
                </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        </TileLink>

        {/* Top priority reviews (top 3 by weighted workload) */}
        <TileLink to={workloadsFailed ? undefined : '/bookkeeping?view=workload'}>
          <Card style={{ padding: '20px', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={iconChipStyle(sem.purple)}>
                <People24Regular />
              </div>
              <div style={{ flex: 1 }}>
                <Text
                  size={200}
                  style={{ color: tokens.colorNeutralForeground3, display: 'block' }}
                >
                  {t('dashboard.widgets.topPriority')}
                </Text>
                {workloadsFailed ? (
                  <div style={{ marginTop: '6px' }}><LoadFailed onRetry={() => refetchWorkloads()} /></div>
                ) : workloads && workloads.length > 0 ? (
                  <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {[...workloads]
                      .sort(
                        (a, b) =>
                          (b.priorityWeightedWorkload ?? 0) -
                          (a.priorityWeightedWorkload ?? 0)
                      )
                      .slice(0, 3)
                      .map((bk) => (
                        <div
                          key={bk.bookkeeperId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <Text size={300} weight="semibold" style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {bk.bookkeeperName}
                          </Text>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                            {Math.round(bk.priorityWeightedWorkload ?? 0)}
                          </Text>
                        </div>
                      ))}
                  </div>
                ) : (
                  <Text size={600} weight="semibold" style={{ display: 'block' }}>
                    -
                  </Text>
                )}
              </div>
            </div>
          </Card>
        </TileLink>
      </div>
    </div>
  );
}
